// Shrine owns variants, gallery, quantity radios, add-to-cart and drawer. This
// enhancement only mirrors native selection in the sticky display/visibility.
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-sl-copy-coupon]');
    if (!button || button.disabled) return;
    const status = button.parentElement.querySelector('.sl-coupon-status');
    button.disabled = true;
    status.textContent = '';
    try {
      await navigator.clipboard.writeText(button.dataset.code);
      button.classList.add('success');
      status.textContent = button.querySelector('.announcement-bar__discount__success').textContent.trim();
      setTimeout(() => button.classList.remove('success'), 2000);
    } catch {
      status.textContent = status.dataset.copyFailed;
    } finally { button.disabled = false; }
  });
  // Keep the complete heading in the accessibility tree. Only the visual copy
  // types/deletes, with the reference timing and a stable reserved line height.
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  document.querySelectorAll('[data-sl-typing]').forEach(heading => {
    const output = heading.querySelector('.sl-typing-animated');
    const text = Array.from(heading.querySelector('.sl-typing-static').textContent);
    let timer, index = 0, deleting = false, visible = false;
    const stop = () => { clearTimeout(timer); heading.classList.remove('sl-typing-active'); };
    const tick = () => {
      if (motion.matches || !visible || document.hidden || !heading.isConnected) return stop();
      heading.classList.add('sl-typing-active');
      index += deleting ? -1 : 1;
      output.textContent = text.slice(0, index).join('');
      let delay = deleting ? 10 : 50;
      if (index === text.length) { deleting = true; delay = 500; }
      else if (index === 0) { deleting = false; delay = 500; }
      timer = setTimeout(tick, delay);
    };
    const restart = () => { clearTimeout(timer); if (!motion.matches && visible && !document.hidden) tick(); else stop(); };
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; restart(); });
    observer.observe(heading);
    motion.addEventListener('change', restart);
    document.addEventListener('visibilitychange', restart);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(heading)) { stop(); observer.disconnect(); motion.removeEventListener('change', restart); document.removeEventListener('visibilitychange', restart); }
    });
  });
  const animations = new WeakMap();
  const openDetails = (details, open) => {
    const start = details.getBoundingClientRect().height;
    animations.get(details)?.animation.cancel();
    const summary = details.querySelector('summary');
    const styles = getComputedStyle(details);
    if (open) details.open = true;
    summary.setAttribute('aria-expanded', String(open));
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { details.open = open; animations.delete(details); return; }
    const end = open ? details.scrollHeight : summary.getBoundingClientRect().height + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom) + parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const animation = details.animate([{ height: `${start}px` }, { height: `${end}px` }], { duration: 210, easing: 'cubic-bezier(.2,.7,.3,1)' });
    animations.set(details, { animation, open });
    animation.onfinish = () => { details.open = open; animations.delete(details); };
  };
  document.querySelectorAll('.sl-faq-list, .sl-closer-grid').forEach(group => {
    group.querySelectorAll('details').forEach(details => {
      const summary = details.querySelector('summary');
      summary.setAttribute('aria-expanded', String(details.open));
      summary.addEventListener('click', event => {
        event.preventDefault();
        const next = !(animations.get(details)?.open ?? details.open);
        if (next) group.querySelectorAll('details[open]').forEach(other => { if (other !== details) openDetails(other, false); });
        openDetails(details, next);
      });
    });
  });
  document.querySelectorAll('[data-sl-purchase]').forEach(info => {
    const form = info.querySelector('product-form form');
    const button = form?.querySelector('[name="add"]');
    const sticky = info.querySelector('sticky-atc');
    const prices = info.querySelector('template[data-sl-prices]');
    const breaks = info.querySelector('quantity-breaks');
    if (!form || !button || !sticky || !prices || !breaks) return;
    const summary = document.createElement('div');
    summary.className = 'sl-sticky-selection';
    summary.setAttribute('aria-live', 'polite');
    sticky.querySelector('.sticky-atc__left__content')?.append(summary);
    sticky.dataset.slVisible = 'false';
    let pending = false;
    const sync = () => {
      pending = false;
      const id = form.querySelector('[name="id"]')?.value;
      const variant = Array.from(prices.content.children).find(v => v.dataset.variantId === id);
      if (!variant) return;
      const radios = Array.from(breaks.querySelectorAll('input[name="quantity"]'));
      const { min, step, max, available } = variant.dataset;
      radios.forEach(radio => {
        const qty = Number(radio.value);
        radio.disabled = available !== 'true' || qty < Number(min) || (qty - Number(min)) % Number(step) !== 0 || Boolean(max && qty > Number(max));
      });
      let selected = radios.find(r => r.checked);
      if (selected?.disabled && available === 'true') {
        const allowed = radios.find(r => !r.disabled);
        if (allowed) { allowed.checked = true; allowed.dispatchEvent(new Event('change', { bubbles: true })); selected = allowed; }
      }
      const total = variant.querySelector(`[data-quantity="${selected?.value || 1}"]`)?.textContent.trim() || '';
      // Shopify/Liquid already formats the totals; never infer currency from text.
      radios.forEach(radio => {
        const snapshot = variant.querySelector(`[data-quantity="${radio.value}"]`);
        const price = snapshot?.textContent.trim();
        const label = Array.from(breaks.querySelectorAll('label')).find(l => l.htmlFor === radio.id);
        const target = label?.querySelector('.quantity-break__price');
        if (target && price && target.textContent.trim() !== price) target.textContent = price;
        for (const [selector, value] of [['.sl-bundle-unit', snapshot?.dataset.each], ['.quantity-break__caption', snapshot?.dataset.each], ['.sl-volume-compare', snapshot?.dataset.compare || '']]) {
          const node = label?.querySelector(selector);
          if (node && value !== undefined && node.textContent.trim() !== value) node.textContent = value;
        }
        const image = label?.querySelector('.quantity-break__image img');
        if (image && variant.dataset.imageSrc && image.getAttribute('src') !== variant.dataset.imageSrc) image.setAttribute('src', variant.dataset.imageSrc);
      });
      const quantityLabel = breaks.querySelector(`label[for="${selected?.id}"] .quantity-break__label-text`)?.textContent.trim() || '';
      const variantLabel = prices.content.children.length > 1 ? variant.dataset.variantLabel : '';
      const text = [variantLabel, quantityLabel, `${prices.dataset.totalLabel} ${total}`].filter(Boolean).join(' · ');
      if (summary.textContent !== text) summary.textContent = text;
      const rect = button.getBoundingClientRect();
      sticky.dataset.slVisible = String(rect.bottom <= 0 && !document.querySelector('cart-drawer.active'));
    };
    const schedule = () => { if (!pending) { pending = true; requestAnimationFrame(sync); } };
    info.addEventListener('change', schedule);
    new MutationObserver(schedule).observe(button, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
    new MutationObserver(schedule).observe(breaks, { childList: true, subtree: true });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(schedule).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    new IntersectionObserver(schedule).observe(button);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  });
});
